/** Last calendar month allowed for monthly installment debits (December = 12). */
export const LAST_MONTHLY_INSTALLMENT_MONTH = 12
export const MAX_WEEKLY_INSTALLMENT_PAYMENTS = 24
export const MAX_MONTHLY_INSTALLMENT_PAYMENTS_CAP = 6

function getMonth1Indexed(date) {
  return date.getMonth() + 1
}

/** Current calendar month in America/New_York (matches business billing timezone). */
function getCurrentMonthInEastern(referenceDate = new Date()) {
  const month = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'numeric'
  }).format(referenceDate)

  return Number(month)
}

/**
 * Max monthly installments based on the current calendar month.
 * June → 6 (capped), July → 5, … November → 1, December → 1, January+ → 0.
 * Must stay in sync with getMaxMonthlyInstallmentsFromToday in setc-portal-apis PaymentInstallments.ts.
 */
export function getMaxMonthlyInstallmentsFromToday(referenceDate = new Date()) {
  const currentMonth = getCurrentMonthInEastern(referenceDate)
  if (currentMonth > LAST_MONTHLY_INSTALLMENT_MONTH)
    return 0
  if (currentMonth === LAST_MONTHLY_INSTALLMENT_MONTH)
    return 1
  return LAST_MONTHLY_INSTALLMENT_MONTH - currentMonth
}

/**
 * Max monthly installments for a chosen start date so the schedule ends by December.
 * Must stay in sync with getMaxMonthlyInstallmentsFromStartDate in setc-portal-apis PaymentInstallments.ts.
 */
export function getMaxMonthlyInstallmentsFromStartDate(startDate, referenceDate = new Date()) {
  if (!startDate)
    return 0

  const startMonth = getMonth1Indexed(startDate)
  const startYear = startDate.getFullYear()
  const planYear = referenceDate.getFullYear()

  if (startYear !== planYear || startMonth > LAST_MONTHLY_INSTALLMENT_MONTH)
    return 0
  if (startMonth === LAST_MONTHLY_INSTALLMENT_MONTH)
    return 1
  return LAST_MONTHLY_INSTALLMENT_MONTH - startMonth
}

/** Effective max monthly payment count (capped at 6). Weekly callers should use MAX_WEEKLY_INSTALLMENT_PAYMENTS. */
export function getMaxMonthlyInstallmentPayments(startDate, referenceDate = new Date()) {
  const fromToday = getMaxMonthlyInstallmentsFromToday(referenceDate)
  if (fromToday <= 0)
    return 0

  let max = fromToday
  if (startDate) {
    const fromStart = getMaxMonthlyInstallmentsFromStartDate(startDate, referenceDate)
    if (fromStart <= 0)
      return 0
    max = Math.min(max, fromStart)
  }

  return Math.min(MAX_MONTHLY_INSTALLMENT_PAYMENTS_CAP, max)
}

export function getInstallmentPaymentCountOptions(paymentType, startDate, referenceDate = new Date()) {
  if (paymentType === 'weekly')
    return Array.from({ length: MAX_WEEKLY_INSTALLMENT_PAYMENTS }, (_, i) => i + 1)

  const maxMonthly = startDate
    ? getMaxMonthlyInstallmentPayments(startDate, referenceDate)
    : getMaxMonthlyInstallmentsFromToday(referenceDate)

  if (maxMonthly <= 0)
    return []

  return Array.from({ length: maxMonthly }, (_, i) => i + 1)
}

export function isValidMonthlyInstallmentSchedule(startDate, numPayments, referenceDate = new Date()) {
  const count = Number(numPayments)
  if (!startDate || !Number.isInteger(count) || count < 1)
    return false

  const max = getMaxMonthlyInstallmentPayments(startDate, referenceDate)
  return max > 0 && count <= max
}

/** Latest day a monthly plan may start (Dec 28 — monthly dates are limited to the 1st–28th). */
export function getLatestMonthlyInstallmentStartDate(referenceDate = new Date()) {
  return new Date(referenceDate.getFullYear(), LAST_MONTHLY_INSTALLMENT_MONTH - 1, 28)
}

export function getMonthlyInstallmentDate(startDate, paymentIndex) {
  const nextDate = new Date(startDate)
  nextDate.setMonth(startDate.getMonth() + paymentIndex)
  return nextDate
}
